// Microphone button that dictates speech into a text field using the browser's
// Web Speech API (Chrome/Edge). Keeps prior value and appends dictated text.
import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { cn } from "@/lib/cn";

interface Props {
  value: string;
  onChange: (next: string) => void;
  lang?: string;              // BCP-47 — defaults to browser locale then en-US
  className?: string;
  title?: string;             // tooltip override
}

// Browser provides either SpeechRecognition or webkitSpeechRecognition; type loosely.
function getRecognition(): any {
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function VoiceInput({ value, onChange, lang, className, title }: Props) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef = useRef<any>(null);
  const baseRef = useRef<string>("");

  useEffect(() => {
    const Ctor = getRecognition();
    if (!Ctor) { setSupported(false); return; }
  }, []);

  function start() {
    const Ctor = getRecognition();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = lang ?? navigator.language ?? "en-US";
    rec.continuous = true;       // keep listening until user stops
    rec.interimResults = true;   // stream partial results for instant feedback

    // Snapshot the current value so we append to it cleanly.
    baseRef.current = value ? value.replace(/\s+$/, "") + " " : "";

    rec.onresult = (e: any) => {
      let finalText = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      // While interim results come in, show them live; once finalised they stay.
      const newBase = finalText ? baseRef.current + finalText : baseRef.current;
      if (finalText) baseRef.current = newBase + " ";
      onChange((interim ? newBase + interim : newBase).replace(/\s+/g, " ").trimStart());
    };

    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);

    recRef.current = rec;
    setListening(true);
    rec.start();
  }

  function stop() {
    recRef.current?.stop();
    setListening(false);
  }

  if (!supported) {
    return (
      <button
        type="button"
        disabled
        title="Voice input isn't supported in this browser. Try Chrome or Edge."
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-md border text-muted-foreground/40",
          className,
        )}
      >
        <MicOff className="h-3.5 w-3.5" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => (listening ? stop() : start())}
      title={title ?? (listening ? "Stop dictation" : "Dictate")}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors",
        listening
          ? "border-red-500 bg-red-500/10 text-red-600 animate-pulse"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      <Mic className="h-3.5 w-3.5" />
    </button>
  );
}
